import Dexie, { Table } from 'dexie';
import type { ProductDataType } from '@am3nd-org/pointcraft-types';

import type { BusinessV3Item, AdminBusinessRow } from 'src/sections/admin-page/ListView/Businesses/types';

import { InventoryStats, SystemConfigType } from './types';

function toInventoryRow(product: ProductDataType): InventoryRow {
  const { deletedAt } = product;
  let deletedAtStr: string | null = null;
  if (deletedAt) {
    deletedAtStr = deletedAt instanceof Date ? deletedAt.toISOString() : String(deletedAt);
  }
  return {
    product_id: product.id,
    identifier: product.identifier ?? '',
    name: product.name ?? '',
    item_category: product.itemCategory ?? 'GENERAL',
    stock: product.currentStockLevel ?? 0,
    price: product.price ?? 0,
    sold: product.sold ?? false,
    deleted_at: deletedAtStr,
    variable_price_lookup_code: product.attributes?.physicalGood?.variablePriceLookupCode ?? '',
    data: product,
  };
}

// Row stored in the Dexie `inventory` object store.
// Scalar fields are indexed for filtering; `data` holds the full product object.
export type InventoryRow = {
  product_id: string;       // PK — equals ProductDataType.id
  identifier: string;
  name: string;
  item_category: string;
  stock: number;
  price: number;
  sold: boolean;
  deleted_at: string | null; // null = active; ISO8601 = deleted
  variable_price_lookup_code: string;
  data: ProductDataType;     // full product stored as a JS object (no serialisation needed in IndexedDB)
};

class DexieService extends Dexie {
  public systemConfig!: Table<SystemConfigType<any>>;

  public inventory!: Table<InventoryRow>;

  public businessInventoryStats!: Table<InventoryStats>;

  public adminBusinesses!: Table<AdminBusinessRow>;

  // Constructor can accept an existing Dexie instance (for injection) or will create one if not provided.
  constructor(dexieInstance?: Dexie) {
    if (dexieInstance) {
      // Use the provided Dexie instance's name to initialize the Dexie base class
      super(dexieInstance.name);
      // Re-declare all versions so Dexie knows the full upgrade path
      this.version(1).stores({ systemConfig: '++id, type, data' });
      this.version(2).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold',
      });
      this.version(3).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold',
        businessInventoryStats: 'id',
      });
      this.version(4)
        .stores({
          systemConfig: '++id, type, data',
          inventory: 'product_id, identifier, name, item_category, sold',
          businessInventoryStats: 'id',
        })
        .upgrade(async (tx) => {
          await tx.table('inventory').toCollection().modify((row: InventoryRow) => {
            row.item_category = (row.data as any)?.itemCategory ?? 'GENERAL';
          });
        });
      this.version(5)
        .stores({
          systemConfig: '++id, type, data',
          inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
          businessInventoryStats: 'id',
        })
        .upgrade(async (tx) => {
          await tx.table('inventory').toCollection().modify((row: InventoryRow) => {
            const physicalGood = (row.data as any)?.attributes?.physicalGood;
            row.variable_price_lookup_code =
              physicalGood?.variablePriceLookupCode
              ?? (physicalGood?.variablePrice === true && ((row.data as any)?.identifier?.length ?? 0) >= 5
                ? String((row.data as any).identifier).substring(0, 5)
                : '');
          });
        });
      this.version(6).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
      });
    } else {
      super('MyDatabase');
      this.version(1).stores({ systemConfig: '++id, type, data' });
      this.version(2).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold',
      });
      this.version(3).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold',
        businessInventoryStats: 'id',
      });
      this.version(4)
        .stores({
          systemConfig: '++id, type, data',
          inventory: 'product_id, identifier, name, item_category, sold',
          businessInventoryStats: 'id',
        })
        .upgrade(async (tx) => {
          await tx.table('inventory').toCollection().modify((row: InventoryRow) => {
            row.item_category = (row.data as any)?.itemCategory ?? 'GENERAL';
          });
        });
      this.version(5)
        .stores({
          systemConfig: '++id, type, data',
          inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
          businessInventoryStats: 'id',
        })
        .upgrade(async (tx) => {
          await tx.table('inventory').toCollection().modify((row: InventoryRow) => {
            const physicalGood = (row.data as any)?.attributes?.physicalGood;
            row.variable_price_lookup_code =
              physicalGood?.variablePriceLookupCode
              ?? (physicalGood?.variablePrice === true && ((row.data as any)?.identifier?.length ?? 0) >= 5
                ? String((row.data as any).identifier).substring(0, 5)
                : '');
          });
        });
      this.version(6).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
      });
    }
    this.systemConfig = this.table('systemConfig');
    this.inventory = this.table('inventory');
    this.businessInventoryStats = this.table('businessInventoryStats');
    this.adminBusinesses = this.table('adminBusinesses');
  }

  public async initializeDB(): Promise<void> {
    try {
      if (!this.isOpen()) {
        await this.open();
        console.log('DexieService: Database opened in initializeDB');
      }
    } catch (error) {
      console.error('DexieService: Error opening DB in initializeDB:', error);
    }
  }

  public async closeConnection(): Promise<void> {
    try {
      this.close();
      console.log('DexieService: Database connection closed');
    } catch (error) {
      console.error('Error closing Dexie database:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // system_config CRUD
  // ---------------------------------------------------------------------------

  public async updateOrCreateSystemConfig<T>(type: string, data: T): Promise<void> {
    const existing = await this.systemConfig.where({ type }).first();
    if (existing) {
      await this.systemConfig.update(existing.id!, { data });
    } else {
      await this.systemConfig.add({ type, data });
    }
  }

  public async getSystemConfig<T>(type: string): Promise<SystemConfigType<T> | null> {
    try {
      const result = await this.systemConfig.get({ type });
      return result ?? null;
    } catch (error) {
      console.error('Error getting system config (Dexie):', error);
      return null;
    }
  }

  public async getAllSystemConfigs<T>(): Promise<SystemConfigType<T>[]> {
    try {
      return await this.systemConfig.toArray();
    } catch (error) {
      console.error('Error getting all system configs (Dexie):', error);
      return [];
    }
  }

  public async deleteSystemConfig(type: string): Promise<void> {
    await this.systemConfig.where({ type }).delete();
  }

  public async deleteAllSystemConfigs(): Promise<void> {
    await this.systemConfig.clear();
  }

  // ---------------------------------------------------------------------------
  // inventory CRUD
  // ---------------------------------------------------------------------------

  // No-op: Dexie creates the table via the schema declaration above.
  // eslint-disable-next-line class-methods-use-this
  public ensureInventoryTable(): Promise<void> {
    return Promise.resolve();
  }

  public async upsertInventoryItem(product: ProductDataType): Promise<void> {
    await this.inventory.put(toInventoryRow(product));
  }

  public async bulkUpsertInventory(products: ProductDataType[]): Promise<void> {
    if (products.length === 0) return;
    await this.inventory.bulkPut(products.map((p) => toInventoryRow(p)));
  }

  public async hardDeleteInventoryItem(productId: string): Promise<void> {
    await this.inventory.delete(productId);
  }

  // IndexedDB has no LIKE operator — we use a JS filter pass after limiting to active rows.
  // For catalogs up to ~10k items this takes <50ms; acceptable for the V4 inventory page.
  public async queryInventoryPage(params: {
    page: number;
    pageSize: number;
    search?: string;
    category?: string;
  }): Promise<{ items: ProductDataType[]; total: number }> {
    const { page, pageSize, search, category } = params;
    const term = search?.toLowerCase().trim();
    const catFilter = category && category !== 'all' ? category.trim().toLowerCase() : null;

    const collection = this.inventory.filter((row) => {
      if (row.deleted_at !== null) return false;
      if (catFilter && row.item_category?.trim().toLowerCase() !== catFilter) return false;
      if (!term) return true;
      return (
        row.name.toLowerCase().includes(term) ||
        row.identifier.toLowerCase().includes(term)
      );
    });

    const [total, rows] = await Promise.all([
      collection.count(),
      collection
        .offset(page * pageSize)
        .limit(pageSize)
        .toArray(),
    ]);

    return { items: rows.map((r) => r.data), total };
  }

  public async countInventory(): Promise<number> {
    return this.inventory.filter((row) => row.deleted_at === null).count();
  }

  public async sumInventoryStock(): Promise<number> {
    let sum = 0;
    await this.inventory
      .filter((row) => row.deleted_at === null)
      .each((row) => {
        sum += row.stock;
      });
    return sum;
  }

  public async clearAllInventory(): Promise<void> {
    await this.inventory.clear();
  }

  public async getInventoryById(productId: string): Promise<ProductDataType | null> {
    const row = await this.inventory.get(productId);
    if (!row || row.deleted_at !== null) return null;
    return row.data;
  }

  public async getInventoryByIdentifier(identifier: string): Promise<ProductDataType | null> {
    const row = await this.inventory
      .where('identifier')
      .equals(identifier)
      .filter((r) => r.deleted_at === null)
      .first();
    return row?.data ?? null;
  }

  public async getInventoryByVariablePriceLookupCode(lookupCode: string): Promise<ProductDataType | null> {
    const row = await this.inventory
      .where('variable_price_lookup_code')
      .equals(lookupCode)
      .filter((r) => r.deleted_at === null)
      .first();
    return row?.data ?? null;
  }

  // ---------------------------------------------------------------------------
  // business_inventory_stats — derived cache
  // ---------------------------------------------------------------------------

  public async getInventoryCategoryCounts(): Promise<Array<{ category: string; count: number }>> {
    const counts = new Map<string, number>();
    await this.inventory
      .filter((row) => row.deleted_at === null)
      .each((row) => {
        const cat = row.item_category?.trim() || 'GENERAL';
        counts.set(cat, (counts.get(cat) ?? 0) + 1);
      });
    return Array.from(counts.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: 'base' }));
  }

  public async recalculateInventoryStats(): Promise<InventoryStats> {
    let totalItems = 0;
    let totalActiveItems = 0;
    let totalDeletedItems = 0;
    let totalStockQuantity = 0;
    const counts = new Map<string, number>();

    await this.inventory.each((row) => {
      totalItems += 1;
      if (row.deleted_at === null) {
        totalActiveItems += 1;
        totalStockQuantity += row.stock;
        const cat = row.item_category?.trim() || 'GENERAL';
        counts.set(cat, (counts.get(cat) ?? 0) + 1);
      } else {
        totalDeletedItems += 1;
      }
    });

    const categoryCounts = Array.from(counts.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: 'base' }));

    const stats: InventoryStats = {
      id: 'inventory_stats',
      totalItems,
      totalActiveItems,
      totalDeletedItems,
      totalStockQuantity,
      categoryCounts,
      lastCalculatedAt: new Date().toISOString(),
    };

    await this.businessInventoryStats.put(stats);
    return stats;
  }

  public async getInventoryStats(options?: {
    recalculateIfMissing?: boolean;
    forceRecalculate?: boolean;
  }): Promise<InventoryStats> {
    const { recalculateIfMissing = true, forceRecalculate = false } = options ?? {};

    if (forceRecalculate) {
      return this.recalculateInventoryStats();
    }

    try {
      const cached = await this.businessInventoryStats.get('inventory_stats');
      if (cached) return cached;

      if (recalculateIfMissing) {
        return await this.recalculateInventoryStats();
      }
    } catch (error) {
      console.error('Error in getInventoryStats (Dexie):', error);
    }

    return {
      id: 'inventory_stats',
      totalItems: 0,
      totalActiveItems: 0,
      totalDeletedItems: 0,
      totalStockQuantity: 0,
      categoryCounts: [],
      lastCalculatedAt: new Date().toISOString(),
    };
  }
  // ---------------------------------------------------------------------------
  // admin_businesses — full-replace cache for the admin businesses list
  // ---------------------------------------------------------------------------

  public async bulkReplaceAdminBusinesses(businesses: BusinessV3Item[]): Promise<void> {
    await this.transaction('rw', this.adminBusinesses, async () => {
      await this.adminBusinesses.clear();
      if (businesses.length > 0) {
        await this.adminBusinesses.bulkPut(
          businesses.map((b) => ({
            id: b.id,
            name: b.name,
            accountNumber: b.accountNumber,
            isAccountActive: b.isAccountActive,
            data: b,
          }))
        );
      }
    });
  }

  public async getAllAdminBusinesses(): Promise<BusinessV3Item[]> {
    const rows = await this.adminBusinesses.toArray();
    return rows.map((r) => r.data);
  }

  public async clearAdminBusinesses(): Promise<void> {
    await this.adminBusinesses.clear();
  }
}

export default DexieService;
